import React, { useState } from 'react';
import { UserProductConsumptionStat } from '../../hooks/admin/useStatistics';
import TabNavigation, { TabId } from '../statistics/TabNavigation';
import UserStatisticsSection from '../statistics/UserStatisticsSection';
import ProductSalesStatisticsSection from '../statistics/ProductSalesStatisticsSection';
import UserProductConsumptionSection from '../statistics/UserProductConsumptionSection';
import UserDepartmentOfficeDetailSection from '../statistics/UserDepartmentOfficeDetailSection';

// Types
interface TopUser {
  user_id: number;
  user_name: string;
  user_surname: string;
  management: string;
  office: string;
  order_count: number;
  total_spent: number;
  average_order_amount: number;
}

interface UserStatsByDepartment {
  management: string;
  user_count: number;
  total_orders: number;
  total_spent: number;
  average_orders_per_user: number;
  average_spent_per_user: number;
}

interface UserStatsByOffice {
  office: string;
  management: string;
  user_count: number;
  total_orders: number;
  total_spent: number;
  average_orders_per_user: number;
  average_spent_per_user: number;
}

interface MostActiveUser {
  user_id: number;
  user_name: string;
  user_surname: string;
  management: string;
  office: string;
  recent_orders: number;
  recent_spent: number;
  last_order_date: string;
}

interface TopProduct {
  product_id: number;
  product_name: string;
  total_quantity: number;
  total_revenue: number;
  order_count: number;
  average_quantity_per_order: number;
}

interface ProductSalesByCategory {
  category_name: string;
  total_quantity: number;
  total_revenue: number;
  product_count: number;
  average_quantity_per_product: number;
}

interface ProductSalesTrend {
  date: string;
  total_quantity: number;
  total_revenue: number;
  order_count: number;
  unique_products: number;
}

interface MostPopularProduct {
  product_id: number;
  product_name: string;
  unique_users: number;
  total_quantity: number;
  total_revenue: number;
  average_quantity_per_user: number;
}

interface ProductSalesByDepartment {
  management: string;
  total_quantity: number;
  total_revenue: number;
  unique_users: number;
  average_quantity_per_user: number;
}

interface StatisticsDashboardProps {
  // User Statistics
  topUsersByOrders: TopUser[];
  topUsersBySpent: TopUser[];
  userStatsByDepartment: UserStatsByDepartment[];
  userStatsByOffice: UserStatsByOffice[];
  mostActiveUsers: MostActiveUser[];
  
  // Product Sales Statistics
  topProductsByQuantity: TopProduct[];
  topProductsByRevenue: TopProduct[];
  productSalesByCategory: ProductSalesByCategory[];
  productSalesTrends: ProductSalesTrend[];
  mostPopularProducts: MostPopularProduct[];
  productSalesByDepartment: ProductSalesByDepartment[];
  
  // User Product Consumption Statistics
  userProductConsumptionStats: UserProductConsumptionStat[];
}

// Utility function to merge user rows
const mergeUserRows = <T extends { 
  user_id: number; 
  user_name: string; 
  user_surname: string; 
  order_count?: number; 
  total_spent?: number; 
}>(rows: T[]): T[] => {
  const userMap = new Map<number, T>();
  
  rows.forEach(row => {
    if (!userMap.has(row.user_id)) {
      userMap.set(row.user_id, { ...row });
    } else {
      const existing = userMap.get(row.user_id)!;
      userMap.set(row.user_id, {
        ...existing,
        order_count: (existing.order_count || 0) + (row.order_count || 0),
        total_spent: (existing.total_spent || 0) + (row.total_spent || 0),
      });
    }
  });
  
  return Array.from(userMap.values());
};

export const StatisticsDashboard: React.FC<StatisticsDashboardProps> = ({
  // User Statistics
  topUsersByOrders,
  topUsersBySpent,
  userStatsByDepartment,
  userStatsByOffice,
  mostActiveUsers,
  
  // Product Sales Statistics
  topProductsByQuantity,
  topProductsByRevenue,
  productSalesByCategory,
  productSalesTrends,
  mostPopularProducts,
  productSalesByDepartment,
  
  // User Product Consumption Statistics
  userProductConsumptionStats,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('users');

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
  };

  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'users':
        return (
          <UserStatisticsSection
            topUsersByOrders={mergeUserRows(topUsersByOrders)}
            topUsersBySpent={mergeUserRows(topUsersBySpent)}
            userStatsByDepartment={userStatsByDepartment}
            userStatsByOffice={userStatsByOffice}
            mostActiveUsers={mostActiveUsers}
          />
        );
      case 'userDetails':
        return (
          <UserDepartmentOfficeDetailSection
            topUsersByOrders={topUsersByOrders}
            userStatsByOffice={userStatsByOffice}
          />
        );
      case 'products':
        return (
          <ProductSalesStatisticsSection
            topProductsByQuantity={topProductsByQuantity}
            topProductsByRevenue={topProductsByRevenue}
            productSalesByCategory={productSalesByCategory}
            productSalesTrends={productSalesTrends}
            mostPopularProducts={mostPopularProducts}
            productSalesByDepartment={productSalesByDepartment}
          />
        );
      case 'consumption':
        return (
          <UserProductConsumptionSection 
            userProductConsumptionStats={userProductConsumptionStats}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <TabNavigation 
        activeTab={activeTab} 
        onTabChange={handleTabChange} 
      />
      
      <div className="space-y-8">
        {renderActiveTabContent()}
      </div>
    </div>
  );
}; 